import { useLocalization } from '@lib/localization';
import { Button, Dialog, Divider, IconButton, Stack, Typography } from '@mui/material';
import { buttonStyles } from '@pages/CustomPoiTypes.styles';
import { dialogBodyStyles } from '@pages/ReportDesigns.styles';
import { dialogCloseStyles, dialogFooterStyles, dialogHeaderStyles, dialogPaperStyles } from '@pages/Teams.styles';
import { useId } from 'react';
import { TimesIcon } from 'react-line-awesome';

interface Props {
  open: boolean;
  title: string;
  submitLabel: string;
  /** Whether the fields hold something that can be sent. */
  canSubmit: boolean;
  saving: boolean;
  onClose: () => void;
  onSubmit: () => void;
  children: React.ReactNode;
}

/** The frame the design dialogs share: a title, their fields, Cancel and the one action. */
const DesignDialog: React.FC<Props> = ({
  open, title, submitLabel, canSubmit, saving, onClose, onSubmit, children,
}) => {
  const { t } = useLocalization();
  // What the dialog is named by: MUI's own title component would carry an id by itself, the
  // title drawn here has to be given one.
  const titleId = useId();

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      aria-labelledby={titleId}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { sx: { ...dialogPaperStyles, maxWidth: 480 } } }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={dialogHeaderStyles}>
        <Typography id={titleId} sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.4 }}>{title}</Typography>
        <IconButton
          size="small"
          aria-label={t('Common_Close')}
          onClick={onClose}
          sx={dialogCloseStyles}
          disabled={saving}
        >
          <TimesIcon />
        </IconButton>
      </Stack>

      <Divider />

      <Stack spacing={2} sx={dialogBodyStyles}>
        {children}
      </Stack>

      <Divider />

      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={dialogFooterStyles}>
        <Button color="inherit" size="small" onClick={onClose} sx={buttonStyles} disabled={saving}>
          {t('Common_Cancel')}
        </Button>
        <Button
          variant="contained"
          size="small"
          disableElevation
          onClick={onSubmit}
          sx={buttonStyles}
          disabled={!canSubmit || saving}
        >
          {submitLabel}
        </Button>
      </Stack>
    </Dialog>
  );
};

export default DesignDialog;
